/**
 * 画像フィールドエディタ — ボタンクリックでギャラリーモーダルを開く。
 * モーダルから過去画像選択 / 削除 / 新規アップロード が可能。
 */
import { ImageIcon, X } from 'lucide-react';
import { useState } from 'react';

import { ImageGalleryDialog } from '@/components/common/ImageGalleryDialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/input';

interface Props {
  label: string;
  value: string | null;
  onChange: (v: string | null) => void;
  /** 旧シグネチャ互換: ファイル直接アップロード（未使用、画像はモーダル経由） */
  onUpload?: (file: File) => Promise<string>;
  /** Storage パス（landing/{landingId}）。指定しないとシステム共有画像を使う。 */
  storagePath?: string;
}

export function ImageFieldEditor({ label, value, onChange, storagePath }: Props) {
  const [galleryOpen, setGalleryOpen] = useState(false);

  return (
    <div>
      <Label className="text-xs">{label}</Label>
      {value ? (
        <div className="flex items-start gap-2 mt-1">
          <button
            type="button"
            onClick={() => setGalleryOpen(true)}
            className="block size-20 rounded border border-[var(--border)] overflow-hidden hover:opacity-80 transition-opacity flex-shrink-0"
            title="画像を変更"
          >
            <img src={value} alt="" className="size-full object-cover" />
          </button>
          <div className="flex-1 flex flex-col gap-1">
            <div className="text-[11px] text-[var(--text-muted)] truncate">
              {value.split('/').pop()?.split('?')[0] ?? value}
            </div>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => setGalleryOpen(true)}>
                <ImageIcon className="size-3 mr-1" />
                変更
              </Button>
              <Button size="sm" variant="ghost" onClick={() => onChange(null)}>
                <X className="size-3" />
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setGalleryOpen(true)}
          className="mt-1 w-full border border-dashed border-[var(--border)] rounded-md p-4 text-center hover:bg-[var(--bg-hover)] transition-colors"
        >
          <ImageIcon className="size-5 mx-auto text-[var(--text-muted)] mb-1" />
          <div className="text-xs text-[var(--text-muted)]">
            クリックして画像を選択 / アップロード
          </div>
        </button>
      )}

      <ImageGalleryDialog
        open={galleryOpen}
        onOpenChange={setGalleryOpen}
        onSelect={(url) => {
          onChange(url);
          setGalleryOpen(false);
        }}
        title={`${label} を選択`}
        storagePath={storagePath}
        maxBytes={10 * 1024 * 1024}
        hintText="10MB 以下 / 画像のみ"
      />
    </div>
  );
}
